<div class="community-thread container-xl py-3" *transloco="let t">
  <div class="mb-3">
    <a class="btn btn-sm btn-link ps-0" [routerLink]="backUrl">
      ← @if (thread()?.channel; as ch) { {{ t('community.thread.back_to_channel', { name: ch.name }) }} } @else { {{ t('community.thread.back') }} }
    </a>
  </div>

  @if (actionError(); as key) {
    <div class="alert alert-danger d-flex justify-content-between align-items-start">
      <span>{{ t(key) }}</span>
      <button type="button" class="btn-close" (click)="actionError.set(null)"></button>
    </div>
  }

  @if (reportNotice(); as outcome) {
    <div class="alert alert-success d-flex justify-content-between align-items-start">
      <span>{{ t(outcome === 'existing' ? 'community.report.already_reported' : 'community.report.thanks') }}</span>
      <button type="button" class="btn-close" (click)="reportNotice.set(null)"></button>
    </div>
  }

  @if (loading() && !thread()) {
    <div class="text-center py-4"><div class="spinner-border" role="status"></div></div>
  } @else if (loadError(); as key) {
    <div class="alert alert-warning">{{ t(key) }}</div>
  } @else if (thread(); as th) {
    @if (isPrivateRoom()) {
      <div class="community-private-room__banner alert alert-dark d-flex align-items-center flex-wrap gap-2 py-2 small">
        <span class="badge bg-dark border">🔐 {{ t('community.private_room.badge') }}</span>
        <span>{{ t('community.private_room.notice') }}</span>
      </div>
    }
    <article class="card mb-3" [class.community-private-room]="isPrivateRoom()" (contextmenu)="blockContextMenu($event)">
      <div class="card-body">
        <div class="d-flex flex-wrap gap-2 mb-2 align-items-center">
          @if (th.channel) {
            <a class="badge bg-info text-dark text-decoration-none" [routerLink]="backUrl"># {{ th.channel.name }}</a>
            @if (isPrivateRoom()) {
              <span class="badge bg-dark">🔐 {{ t('community.private_room.badge') }}</span>
              @if (channelParticipantsReadOnly() && !th.channel.archived && !th.channel.read_only) {
                <span class="badge bg-secondary">{{ t('community.private_room.read_only') }}</span>
              }
            }
            @if (th.channel.archived) {
              <span class="badge bg-secondary">{{ t('community.channel.archive') }}</span>
            } @else if (th.channel.read_only) {
              <span class="badge bg-secondary">{{ t('community.channel.read_only') }}</span>
            }
          }
          @if (th.topic) {
            <span class="badge bg-light text-dark">{{ th.topic.name }}</span>
          }
          @if (th.pinned) {
            <span class="badge bg-warning text-dark">📌 {{ t('community.threads.pinned') }}</span>
          }
          @if (th.locked) {
            <span class="badge bg-secondary">🔒 {{ t('community.threads.locked') }}</span>
          }
          @if (th.masked || (th.deleted_at && canModerate())) {
            <span class="badge bg-secondary">🗑 {{ t('community.threads.deleted') }}</span>
          }
          @if (th.hidden_at && canModerate()) {
            <span class="badge bg-warning text-dark">{{ t('community.threads.hidden') }}</span>
          }
          @if (approvalBadge(); as status) {
            <span class="badge" [class.bg-warning]="status === 'pending'" [class.text-dark]="status === 'pending'"
                  [class.bg-danger]="status === 'rejected'"
                  [title]="t('community.moderation.approval.' + status + '_hint')">
              {{ t('community.moderation.approval.' + status) }}
            </span>
          }
        </div>

        @if (editThreadOpen()) {
          <div class="mb-2">
            <label class="form-label">{{ t('community.threads.title_label') }}</label>
            <input type="text" class="form-control" maxlength="255" [(ngModel)]="editTitleDraft" />
          </div>
          <div class="mb-2">
            <label class="form-label">{{ t('community.threads.body_label') }}</label>
            <app-community-editor [value]="editBodyDraft" (valueChange)="editBodyDraft = $event"
                                 [channelId]="th.channel_id" [canMention]="canMentionHere()"
                                 [canUploadImage]="canUploadImageHere()" [canUploadFile]="canUploadFileHere()"
                                 [canEmbedVideo]="canEmbedVideoHere()" (uploadError)="onEditorUploadError($event)"></app-community-editor>
          </div>
          <div>
            <button type="button" class="btn btn-primary me-2" [disabled]="submitting() || !editTitleDraft.trim() || bodyBlank(editBodyDraft)" (click)="submitEditThread()">
              {{ t('community.thread.actions.edit') }}
            </button>
            <button type="button" class="btn btn-outline-secondary" (click)="cancelEditThread()">
              {{ t('community.threads.cancel') }}
            </button>
            @if (submitting()) {
              <span class="text-muted small ms-2">
                <span class="spinner-border spinner-border-sm me-1" role="status"></span>{{ t('community.language_check.checking') }}
              </span>
            }
          </div>
        } @else {
          <h1 class="h4 mb-2">
            {{ th.masked ? t('community.threads.deleted') : (translatedTitle() ?? th.title) }}
          </h1>
          <div class="community-thread__meta mb-3 text-muted small d-flex align-items-center flex-wrap gap-1">
            @if (th.user; as author) {
              <a class="community-thread__author" [routerLink]="memberLink(author.id)">
                <app-community-avatar [user]="author" [size]="24" />
                <span class="ms-1">{{ author.display_name }}</span>
              </a>
              @if (canModerate()) {
                <button type="button" class="btn btn-link btn-sm p-0 community-thread__mod-link"
                        [title]="t('community.moderation.summary.open')" (click)="openMemberSummary(author.id)">🛡</button>
              }
            } @else {
              —
            }
            <founding-member-badge class="ms-1"
                                   [foundingMember]="th.user?.founding_member"
                                   [memberNumber]="th.user?.founding_member_number" />
            <founding-member-badge class="ms-1" kind="practitioner"
                                   [foundingMember]="th.user?.founding_practitioner"
                                   [memberNumber]="th.user?.founding_practitioner_number" />
            <app-community-badges [badges]="th.user?.badges" />
            @if (th.user?.is_admin) {
              <span class="badge bg-danger ms-1">ADMIN</span>
            }
            @if (authorHidden()) {
              <span class="badge bg-warning text-dark ms-1" [title]="t('community.users.hidden_hint')">
                🚫 {{ t('community.users.hidden') }}
              </span>
            }
            · {{ th.created_at | date:'medium' }}
            @if (th.edited_at) {
              <span class="ms-1 text-muted" [title]="th.edited_at | date:'medium'">
                · {{ t('community.edited') }}
              </span>
            }
          </div>

          @if (th.masked) {
            <em class="text-muted">{{ t('community.threads.deleted') }}</em>
          } @else {
            <app-community-translate class="community-thread__body" kind="thread" [targetId]="th.id"
                                     [originalBody]="th.body || ''" [originalTitle]="th.title"
                                     [sourceLocale]="th.locale" [available]="translationAvailable()" />
            @if (isQaChannel() && solvedPost(); as sp) {
              <div class="community-thread__accepted-answer card border-success mt-3">
                <div class="card-body">
                  <div class="d-flex align-items-center justify-content-between mb-2">
                    <span class="badge bg-success">✓ {{ t('community.qa.accepted_answer') }}</span>
                    @if (canSolve()) {
                      <button type="button" class="btn btn-sm btn-outline-secondary" (click)="unmarkSolved()">
                        {{ t('community.qa.unmark') }}
                      </button>
                    }
                  </div>
                  <div class="small text-muted mb-1">
                    @if (sp.user; as author) { {{ author.display_name }} } @else { — }
                    · {{ sp.created_at | date:'medium' }}
                  </div>
                  <div class="community-thread__accepted-body" [innerHTML]="sp.body | communityBody" appCommunityVideoEmbeds></div>
                </div>
              </div>
            }
            @if (!th.deleted_at) {
              <div class="mt-2">
                <app-community-reactions target="thread"
                                         [targetId]="th.id"
                                         [reactionSet]="reactionSet()"
                                         [reactions]="th.reactions"
                                         [myReactions]="th.my_reactions"
                                         [canReact]="th.can_react === true"
                                         (failed)="onReactionFailed($event)" />
              </div>
            }
          }

          <div class="d-flex flex-wrap gap-2 mt-3">
            @if (th.can_modify && !th.deleted_at) {
              <button type="button" class="btn btn-sm btn-outline-secondary" (click)="openEditThread()">
                {{ t('community.thread.actions.edit') }}
              </button>
            }
            @if (canDelete() && !th.deleted_at) {
              <button type="button" class="btn btn-sm btn-outline-danger" (click)="onDeleteThread()">
                {{ t('community.thread.actions.delete') }}
              </button>
            }
            @if (canReportThread()) {
              <button type="button" class="btn btn-sm btn-outline-secondary" (click)="openReport('CommunityThread', th.id)">
                🚩 {{ t('community.report.action') }}
              </button>
            }
            @if (canPin()) {
              @if (!th.pinned) {
                <button type="button" class="btn btn-sm btn-outline-warning" (click)="onPin(true)">
                  📌 {{ t('community.thread.actions.pin') }}
                </button>
              } @else {
                <button type="button" class="btn btn-sm btn-outline-warning" (click)="onPin(false)">
                  {{ t('community.thread.actions.unpin') }}
                </button>
              }
            }
            @if (canModerate()) {
              @if (!th.locked) {
                <button type="button" class="btn btn-sm btn-outline-secondary" (click)="onLock(true)">
                  🔒 {{ t('community.thread.actions.lock') }}
                </button>
              } @else {
                <button type="button" class="btn btn-sm btn-outline-secondary" (click)="onLock(false)">
                  {{ t('community.thread.actions.unlock') }}
                </button>
              }
              @if (!th.hidden_at) {
                <button type="button" class="btn btn-sm btn-outline-warning" (click)="onHide(true)">
                  {{ t('community.thread.actions.hide') }}
                </button>
              } @else {
                <button type="button" class="btn btn-sm btn-outline-warning" (click)="onHide(false)">
                  {{ t('community.thread.actions.unhide') }}
                </button>
              }
              <button type="button" class="btn btn-sm btn-outline-info" (click)="toggleHistory()">
                @if (historyOpen()) { {{ t('community.history.hide') }} }
                @else { {{ t('community.history.show') }} }
              </button>
              @if (canHideAuthor()) {
                @if (!authorHidden()) {
                  <button type="button" class="btn btn-sm btn-outline-danger" (click)="onHideThreadAuthor(true)">
                    🚫 {{ t('community.users.hide') }}
                  </button>
                } @else {
                  <button type="button" class="btn btn-sm btn-outline-success" (click)="onHideThreadAuthor(false)">
                    {{ t('community.users.unhide') }}
                  </button>
                }
              }
              @if (moveTargets().length > 0) {
                <div class="d-flex align-items-center gap-1">
                  <label class="small text-muted mb-0">{{ t('community.channel.move_label') }}</label>
                  <select class="form-select form-select-sm w-auto"
                    [ngModel]="''"
                    (ngModelChange)="moveToChannel($event)">
                    <option value="">{{ t('community.channel.select') }}</option>
                    @for (target of moveTargets(); track target.group.id) {
                      <optgroup [label]="target.group.name">
                        @for (ch of target.channels; track ch.id) {
                          <option [ngValue]="ch.id"># {{ ch.name }}</option>
                        }
                      </optgroup>
                    }
                  </select>
                </div>
              }
            }
          </div>

          @if (historyOpen() && canModerate()) {
            <div class="mt-3">
              <app-community-history target="thread" [targetId]="threadId" [showTitle]="true"></app-community-history>
            </div>
          }
        }
      </div>
    </article>

    @if (!canReply()) {
      <div class="alert" [class.alert-info]="th.channel.archived" [class.alert-warning]="!th.channel.archived">
        {{ t(noReplyNoticeKey()) }}
      </div>
      <app-community-sanction-notice [sanctions]="sanctions()" [warnings]="warnings()" />
    }

    @if (canReply()) {
      <section class="mb-3">
        @if (!replyOpen()) {
          <button type="button" class="btn btn-outline-primary" (click)="openReply()">
            {{ t('community.thread.reply') }}
          </button>
        } @else {
          <div class="card">
            <div class="card-body">
              <app-community-editor [value]="replyDraft" (valueChange)="replyDraft = $event"
                                 [channelId]="th.channel_id" [canMention]="canMentionHere()"
                                 [canUploadImage]="canUploadImageHere()" [canUploadFile]="canUploadFileHere()"
                                 [canEmbedVideo]="canEmbedVideoHere()" (uploadError)="onEditorUploadError($event)"></app-community-editor>
              <div class="mt-2">
                <button type="button" class="btn btn-primary me-2" [disabled]="submitting() || bodyBlank(replyDraft)" (click)="submitReply()">
                  {{ t('community.thread.reply') }}
                </button>
                <button type="button" class="btn btn-outline-secondary" (click)="cancelReply()">
                  {{ t('community.threads.cancel') }}
                </button>
                @if (submitting()) {
                  <span class="text-muted small ms-2">
                    <span class="spinner-border spinner-border-sm me-1" role="status"></span>{{ t('community.language_check.checking') }}
                  </span>
                }
              </div>
            </div>
          </div>
        }
      </section>
    }

    <section class="community-thread__posts" [class.community-private-room]="isPrivateRoom()" (contextmenu)="blockContextMenu($event)">
      @for (node of postTree(); track node.id) {
        <app-community-post
          [post]="node"
          [threadLocked]="th.locked"
          [canReply]="canReply()"
          [reactionSet]="reactionSet()"
          [depth]="0"
          [canSolve]="canSolve()"
          [solvedPostId]="th.solved_post_id ?? null"
          [translationAvailable]="translationAvailable()"
          [channelId]="th.channel_id"
          [canMention]="canMentionHere()"
          [canUploadImage]="canUploadImageHere()"
          [canUploadFile]="canUploadFileHere()"
          [canEmbedVideo]="canEmbedVideoHere()"
          (uploadError)="onEditorUploadError($event)"
          (reply)="onPostReply($event)"
          (actionFailed)="onReactionFailed($event)"
          (edit)="onPostEdit($event)"
          (delete)="onPostDelete($event)"
          (toggleHide)="onPostToggleHide($event)"
          (toggleHideUser)="onToggleHideUser($event)"
          (report)="openReport('CommunityPost', $event)"
          (memberSummary)="openMemberSummary($event)"
          (markSolved)="markSolved($event)"
          (unmarkSolved)="unmarkSolved()">
        </app-community-post>
      }
    </section>
  }

  <!-- Report dialog (threads and posts alike) -->
  @if (reportTarget(); as target) {
    <app-community-reason-modal [titleKey]="target.type === 'CommunityThread' ? 'community.report.title_thread' : 'community.report.title_post'"
                                hintKey="community.report.hint"
                                labelKey="community.report.reason"
                                placeholderKey="community.report.reason_placeholder"
                                confirmKey="community.report.submit"
                                confirmClass="btn-danger"
                                [busy]="reportBusy()"
                                [error]="reportError()"
                                (confirmed)="submitReport($event)"
                                (cancelled)="reportTarget.set(null)" />
  }

  <!-- Mute/unmute an author: the reason lands in the moderation protocol -->
  @if (hideUserTarget(); as target) {
    <app-community-reason-modal [titleKey]="target.hide ? 'community.users.hide_title' : 'community.users.unhide_title'"
                                [titleParams]="{ name: target.name }"
                                [hintKey]="target.hide ? 'community.users.hide_confirm' : null"
                                [confirmKey]="target.hide ? 'community.users.hide' : 'community.users.unhide'"
                                [confirmClass]="target.hide ? 'btn-danger' : 'btn-success'"
                                [busy]="hideUserBusy()"
                                [error]="hideUserError()"
                                (confirmed)="confirmHideUser($event)"
                                (cancelled)="hideUserTarget.set(null)" />
  }

  @if (summaryUserId(); as userId) {
    <app-community-member-summary [userId]="userId" (closed)="summaryUserId.set(null)" (changed)="load()" />
  }

  <!-- Renders itself only when a save ran into a confident language mismatch (§7.3) -->
  <app-community-language-confirm-modal />
</div>
